import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { BannerRoot } from './Setupstepsbanner.style';

// ─── localStorage keys ────────────────────────────────────────────────────────

const LS_KEYS = {
    ads:     'dismiss_step_ads_api',
    expense: 'dismiss_step_expense',
    cogs:    'dismiss_step_cogs_tax',
};

const readDismissed = () => ({
    ads:     localStorage.getItem(LS_KEYS.ads)     === 'true',
    expense: localStorage.getItem(LS_KEYS.expense) === 'true',
    cogs:    localStorage.getItem(LS_KEYS.cogs)    === 'true',
});

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
    {
        id:         1,
        dismissKey: null,
        title:      'Connect Seller Central',
        label:      'Step 01',
        iconClass:  'ki-directbox-default',
        description:'Link your Amazon Selling Partner account. Required before any other step.',
        ctaLabel:   'Connect Seller Central',
        ctaLink:    '/settings/marketplace-credentials?tab=spApi&creds=true',
        mandatory:  true,
    },
    {
        id:         2,
        dismissKey: 'ads',
        title:      'Connect Campaign Manager',
        label:      'Step 02',
        iconClass:  'ki-notification-on',
        description:'Link your Amazon Ads account to track PPC performance and ad spend.',
        ctaLabel:   'Connect Campaign Manager',
        ctaLink:    '/settings/marketplace-credentials?tab=advertising&creds=true',
        mandatory:  false,
    },
    {
        id:         3,
        dismissKey: 'cogs',
        title:      'Add COGS & Tax',
        label:      'Step 03',
        iconClass:  'ki-finance-calculator',
        description:'Enter cost of goods sold and tax rates to enable accurate margin calculations.',
        ctaLabel:   'Add COGS & Tax',
        ctaLink:    '/settings/products',
        mandatory:  false,
    },
    {
        id:         4,
        dismissKey: 'expense',
        title:      'Add Expense Data',
        label:      'Step 04',
        iconClass:  'ki-wallet',
        description:'Add business expenses so profit reports reflect all operational costs.',
        ctaLabel:   'Add Expenses',
        ctaLink:    '/settings/expenses',
        mandatory:  false,
    }
];

const getCompletionStatus = (userData) => ({
    1: userData?.user?.user_data?.is_amz_sp_api_connected     === 1,
    2: userData?.user?.user_data?.is_amz_ads_connected        === 1,
    3: userData?.user?.user_data?.is_expense_data_added       === true,
    4: userData?.user?.user_data?.is_product_cogs_n_tax_added === true,
});

// ─── SetupStepsBanner ─────────────────────────────────────────────────────────

const SetupStepsBanner = ({ userData }) => {
    const completed = useMemo(() => getCompletionStatus(userData), [userData]);
    const [dismissed, setDismissed] = useState(() => readDismissed());
    const [currentStep, setCurrentStep] = useState(1);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const step1Done = completed[1];

    const visibleSteps = useMemo(() =>
            STEPS.filter(s => !completed[s.id] && !(s.dismissKey && dismissed[s.dismissKey])),
        [completed, dismissed]
    );

    const allDone = useMemo(() =>
            completed[1] &&
            (completed[2] || dismissed.ads) &&
            (completed[3] || dismissed.expense) &&
            (completed[4] || dismissed.cogs),
        [completed, dismissed]
    );

    // Auto-advance to first incomplete step
    useEffect(() => {
        if (!step1Done) { setCurrentStep(1); return; }
        if (!visibleSteps.find(s => s.id === currentStep) && visibleSteps[0]) {
            setCurrentStep(visibleSteps[0].id);
        }
    }, [completed, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDismiss = useCallback((dismissKey) => {
        localStorage.setItem(LS_KEYS[dismissKey], 'true');
        setDismissed(prev => ({ ...prev, [dismissKey]: true }));
        const remaining = visibleSteps.filter(s => s.dismissKey !== dismissKey);
        if (remaining.length > 0) setCurrentStep(remaining[0].id);
    }, [visibleSteps]);

    const handlePrev = () => {
        const idx = STEPS.findIndex(s => s.id === currentStep);
        if (idx > 0) setCurrentStep(STEPS[idx - 1].id);
    };

    const handleNext = () => {
        const idx = STEPS.findIndex(s => s.id === currentStep);
        if (idx < STEPS.length - 1) setCurrentStep(STEPS[idx + 1].id);
    };

    const canGoPrev = STEPS.findIndex(s => s.id === currentStep) > 0;
    const canGoNext = STEPS.findIndex(s => s.id === currentStep) < STEPS.length - 1;

    const activeStep    = STEPS.find(s => s.id === currentStep);
    const isCurrentDone = completed[currentStep];
    const isCurrentDis  = activeStep?.dismissKey ? dismissed[activeStep.dismissKey] : false;
    const isLocked      = !step1Done && currentStep > 1;

    const handledCount = STEPS.filter(s =>
        completed[s.id] || (s.dismissKey && dismissed[s.dismissKey])
    ).length;

    if (allDone) return null;

    const stepIconClass = `ki-outline ${isCurrentDone ? 'ki-check-circle' : isCurrentDis ? 'ki-information-5' : activeStep?.iconClass}`;
    const stepIconStateClass = isCurrentDone
        ? 'setup-guide-step-icon--done'
        : isCurrentDis
            ? 'setup-guide-step-icon--skipped'
            : 'setup-guide-step-icon--default';
    const stepDescText = isCurrentDone
        ? 'This step is complete — you\'re all set!'
        : isCurrentDis
            ? 'Skipped. Revisit anytime from Settings.'
            : activeStep?.description;

    return (
        <BannerRoot>
            {/* Desktop Layout (hidden on mobile) */}
            <div className="desktop-layout">
                {/* ── Single compact row for desktop ── */}
                <div className="setup-guide-row">
                    {/* Left — label + progress */}
                    <div className="setup-guide-rail">
                        <div className="setup-guide-rail-title">
                            <i className="ki-outline ki-abstract-14" />
                            <span>Setup Guide</span>
                        </div>
                        {/* Mini progress dots */}
                        <div className="setup-guide-dots">
                            {STEPS.map(s => {
                                const done = completed[s.id];
                                const dis  = s.dismissKey && dismissed[s.dismissKey];
                                const active = s.id === currentStep;
                                const stepLocked = !step1Done && s.id > 1;
                                const colorClass = done || dis
                                    ? 'setup-guide-dot--done'
                                    : active
                                        ? 'setup-guide-dot--active-color'
                                        : 'setup-guide-dot--idle-color';
                                return (
                                    <Tooltip
                                        key={s.id}
                                        title={stepLocked ? 'Connect SP API first' : s.title}
                                        placement="bottom"
                                    >
                                        <button
                                            onClick={() => stepLocked ? undefined : setCurrentStep(s.id)}
                                            className={[
                                                'setup-guide-dot',
                                                active ? 'setup-guide-dot--active' : 'setup-guide-dot--idle',
                                                colorClass,
                                                stepLocked ? 'setup-guide-dot--locked' : '',
                                            ].filter(Boolean).join(' ')}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </div>
                        <div className="setup-guide-rail-meta">
                            {handledCount} / {STEPS.length} done
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="setup-guide-divider-v" />

                    {/* Centre — step content (animated on step change) */}
                    <div key={currentStep} className="setup-guide-content">
                        {/* Step icon */}
                        <span className={`setup-guide-step-icon ${stepIconStateClass}${isLocked ? ' setup-guide-step-icon--locked' : ''}`}>
                            <i className={stepIconClass} />
                        </span>

                        {/* Text */}
                        <div className="setup-guide-text">
                            <div className="setup-guide-label-row">
                                <span className="setup-guide-step-label">{activeStep?.label}</span>
                                {activeStep?.mandatory && (
                                    <span className="setup-guide-badge-mandatory">MANDATORY</span>
                                )}
                                {(isCurrentDone || isCurrentDis) && (
                                    <span className={`setup-guide-badge-status ${isCurrentDone ? 'setup-guide-badge-status--done' : 'setup-guide-badge-status--skipped'}`}>
                                        {isCurrentDone ? 'Done' : 'Skipped'}
                                    </span>
                                )}
                            </div>

                            <div className={`setup-guide-step-title${isLocked ? ' setup-guide-step-title--locked' : ''}`}>
                                {activeStep?.title}
                            </div>

                            <div className={`setup-guide-step-desc${isLocked ? ' setup-guide-step-desc--locked' : ''}`}>
                                {stepDescText}
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="setup-guide-divider-v" />

                    {/* Right — CTA + dismiss + navigation */}
                    <div className="setup-guide-actions">
                        {/* CTA button */}
                        {!isCurrentDone && !isCurrentDis && (
                            isLocked ? (
                                <Tooltip title="Please connect Seller Central first">
                                    <span className="setup-guide-cta setup-guide-cta--disabled">
                                        {activeStep?.ctaLabel}
                                        <i className="ki-outline ki-arrow-right" style={{ fontSize: 10 }} />
                                    </span>
                                </Tooltip>
                            ) : (
                                <Link to={activeStep?.ctaLink} className="setup-guide-cta">
                                    {activeStep?.ctaLabel}
                                    <i className="ki-outline ki-arrow-right" style={{ fontSize: 10 }} />
                                </Link>
                            )
                        )}

                        {/* Don't remind me */}
                        {!activeStep?.mandatory && !isLocked && !isCurrentDone && !isCurrentDis && (
                            <button onClick={() => handleDismiss(activeStep.dismissKey)} className="setup-guide-dismiss">
                                Don't remind me again
                            </button>
                        )}

                        {/* Divider */}
                        <div className="setup-guide-divider-h" />

                        {/* Prev / Next */}
                        <button
                            onClick={handlePrev}
                            disabled={!canGoPrev}
                            className={`setup-guide-nav-btn ${canGoPrev ? 'setup-guide-nav-btn--enabled' : 'setup-guide-nav-btn--disabled'}`}
                        >
                            <i className="ki-outline ki-arrow-left" />
                        </button>

                        <button
                            onClick={handleNext}
                            disabled={!canGoNext}
                            className={`setup-guide-nav-btn ${canGoNext ? 'setup-guide-nav-btn--enabled' : 'setup-guide-nav-btn--disabled'}`}
                        >
                            <i className="ki-outline ki-arrow-right" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Layout (visible on mobile, hidden on desktop) */}
            <div className="mobile-layout">
                {/* Mobile Header */}
                <div className="setup-guide-mobile-header">
                    <div>
                        <div className="setup-guide-rail-title">
                            <i className="ki-outline ki-abstract-14" />
                            <span>Setup Guide</span>
                        </div>
                        <div className="setup-guide-rail-meta">
                            {handledCount} / {STEPS.length} done
                        </div>
                    </div>
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="setup-guide-mobile-toggle"
                    >
                        {mobileMenuOpen ? 'Close' : 'Steps'}
                    </button>
                </div>

                {/* Mobile Content */}
                <div className="setup-guide-mobile-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <span className={`setup-guide-mobile-step-icon ${stepIconStateClass}${isLocked ? ' setup-guide-step-icon--locked' : ''}`}>
                            <i className={stepIconClass} />
                        </span>
                        <div style={{ flex: 1 }}>
                            <div className="setup-guide-label-row">
                                <span className="setup-guide-step-label">{activeStep?.label}</span>
                                {activeStep?.mandatory && (
                                    <span className="setup-guide-badge-mandatory">MANDATORY</span>
                                )}
                                {(isCurrentDone || isCurrentDis) && (
                                    <span className={`setup-guide-badge-status ${isCurrentDone ? 'setup-guide-badge-status--done' : 'setup-guide-badge-status--skipped'}`}>
                                        {isCurrentDone ? 'Done' : 'Skipped'}
                                    </span>
                                )}
                            </div>
                            <div className={`setup-guide-mobile-title${isLocked ? ' setup-guide-step-title--locked' : ''}`}>
                                {activeStep?.title}
                            </div>
                            <div className={`setup-guide-mobile-desc${isLocked ? ' setup-guide-step-desc--locked' : ''}`}>
                                {stepDescText}
                            </div>
                        </div>
                    </div>

                    {/* Mobile Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                        {!isCurrentDone && !isCurrentDis && (
                            isLocked ? (
                                <Tooltip title="Please connect SP API first">
                                    <span className="setup-guide-mobile-cta setup-guide-mobile-cta--disabled">
                                        {activeStep?.ctaLabel}
                                        <i className="ki-outline ki-arrow-right" />
                                    </span>
                                </Tooltip>
                            ) : (
                                <Link to={activeStep?.ctaLink} className="setup-guide-mobile-cta">
                                    {activeStep?.ctaLabel}
                                    <i className="ki-outline ki-arrow-right" />
                                </Link>
                            )
                        )}

                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                            <button
                                onClick={handlePrev}
                                disabled={!canGoPrev}
                                className={`setup-guide-mobile-nav ${canGoPrev ? 'setup-guide-mobile-nav--enabled' : 'setup-guide-mobile-nav--disabled'}`}
                            >
                                <i className="ki-outline ki-arrow-left" /> Previous
                            </button>
                            <button
                                onClick={handleNext}
                                disabled={!canGoNext}
                                className={`setup-guide-mobile-nav ${canGoNext ? 'setup-guide-mobile-nav--enabled' : 'setup-guide-mobile-nav--disabled'}`}
                            >
                                Next <i className="ki-outline ki-arrow-right" />
                            </button>
                        </div>

                        {!activeStep?.mandatory && !isLocked && !isCurrentDone && !isCurrentDis && (
                            <button
                                onClick={() => handleDismiss(activeStep.dismissKey)}
                                className="setup-guide-dismiss"
                                style={{ padding: 6 }}
                            >
                                Don't remind me again
                            </button>
                        )}
                    </div>
                </div>

                {/* Mobile Steps Menu */}
                {mobileMenuOpen && (
                    <>
                        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
                        <div className={`mobile-steps-menu ${mobileMenuOpen ? 'open' : ''}`}>
                            <div style={{ padding: '16px' }}>
                                <div className="mobile-steps-menu-title">All Steps</div>
                                {STEPS.map(step => {
                                    const isComplete = completed[step.id];
                                    const isSkipped = step.dismissKey && dismissed[step.dismissKey];
                                    const isActive = step.id === currentStep;
                                    const isStepLocked = !step1Done && step.id > 1;

                                    return (
                                        <button
                                            key={step.id}
                                            onClick={() => {
                                                if (!isStepLocked) {
                                                    setCurrentStep(step.id);
                                                    setMobileMenuOpen(false);
                                                }
                                            }}
                                            disabled={isStepLocked}
                                            className={[
                                                'setup-guide-mobile-step-btn',
                                                isActive ? 'setup-guide-mobile-step-btn--active' : '',
                                                isStepLocked ? 'setup-guide-mobile-step-btn--locked' : '',
                                            ].filter(Boolean).join(' ')}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <span className="setup-guide-step-icon setup-guide-step-icon--default" style={{ fontSize: 20 }}>
                                                    <i className={`ki-outline ${step.iconClass}`} style={{ fontSize: 20 }} />
                                                </span>
                                                <div style={{ flex: 1 }}>
                                                    <div className="setup-guide-mobile-step-btn-title">{step.title}</div>
                                                    <div className="setup-guide-mobile-step-btn-label">{step.label}</div>
                                                </div>
                                                {(isComplete || isSkipped) && (
                                                    <span className={`setup-guide-badge-status ${isComplete ? 'setup-guide-badge-status--done' : 'setup-guide-badge-status--skipped'}`}>
                                                        {isComplete ? 'Done' : 'Skipped'}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ── Thin progress bar at the very bottom ── */}
            <div className="setup-guide-progress-track">
                <div
                    className="setup-guide-progress-fill"
                    style={{ width: `${(handledCount / STEPS.length) * 100}%` }}
                />
            </div>
        </BannerRoot>
    );
};

export default SetupStepsBanner;
